import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { VerificationAPI, OrderAPI } from '../services/api';
import { useLanguage } from './Navbar';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from { 
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.9);
  }
  to { 
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

// Styled Components
const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${fadeIn} 0.3s ease;
`;

const Modal = styled(motion.div)`
  background: #ffffff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  animation: ${slideIn} 0.3s ease;
`;

const Header = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid #e0e0e0;
  position: relative;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #666666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s ease;
  
  &:hover {
    background: #f5f5f5;
    color: #1a1a1a;
  }
`;

const Content = styled.div`
  padding: 2rem;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const Step = styled.div<{ active: boolean; completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => 
    props.completed ? '#d4af37' :
    props.active ? '#1a1a1a' : '#e0e0e0'
  };
  color: ${props => 
    props.completed || props.active ? '#ffffff' : '#999999'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
`;

const StepLine = styled.div<{ completed: boolean }>`
  width: 40px;
  height: 2px;
  background: ${props => props.completed ? '#d4af37' : '#e0e0e0'};
  transition: all 0.3s ease;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #1a1a1a;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #d4af37;
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
  
  &::placeholder {
    color: #999999;
  }
`;

const VerificationCodeInput = styled(Input)`
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-weight: 600;
`;

const Button = styled.button`
  width: 100%;
  background: #1a1a1a;
  color: #ffffff;
  border: none;
  padding: 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  
  &:hover:not(:disabled) {
    background: #d4af37;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const BackButton = styled.button`
  width: 100%;
  background: transparent;
  color: #666666;
  border: 1px solid #e0e0e0;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  
  &:hover {
    border-color: #1a1a1a;
    color: #1a1a1a;
  }
`;

const ErrorMessage = styled.div`
  background: #ffebee;
  color: #c62828;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.875rem;
`;

const SuccessMessage = styled.div`
  background: #e8f5e8;
  color: #2e7d32;
  padding: 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.875rem;
`;

const InfoBox = styled.div`
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const InfoIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const InfoText = styled.p`
  color: #666666;
  margin: 0;
  line-height: 1.5;
`;

const IbanInfo = styled.div`
  background: #f5f5f5;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const IbanRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  
  &:last-child {
    margin-bottom: 0;
    font-weight: 600;
    font-size: 1rem;
    color: #d4af37;
    border-top: 1px solid #e0e0e0;
    padding-top: 0.75rem;
  }
`;

const IbanLabel = styled.span`
  color: #666666;
`;

const IbanValue = styled.span`
  color: #1a1a1a;
  font-weight: 500;
`;

const FileUploadArea = styled.div`
  border: 2px dashed #d4af37;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  background: rgba(212, 175, 55, 0.05);
  margin: 1.5rem 0;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(212, 175, 55, 0.1);
    border-color: #b8941f;
  }
`;

const FileUploadInput = styled.input`
  display: none;
`;

const FileUploadIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #d4af37;
`;

const FileUploadText = styled.div`
  color: #666666;
  margin-bottom: 0.5rem;
`;

const FileUploadSubtext = styled.div`
  font-size: 0.75rem;
  color: #999999;
`;

const FilePreview = styled.div`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1rem;
  margin: 1rem 0;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FileIcon = styled.div`
  font-size: 1.5rem;
  color: #d4af37;
`;

const FileInfo = styled.div`
  flex: 1;
`;

const FileName = styled.div`
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
`;

const FileSize = styled.div`
  font-size: 0.75rem;
  color: #666666;
`;

const RemoveFileButton = styled.button`
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #cc3333;
  }
`;

interface CheckoutPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (result: { order_id: string; customer_id: string }) => void;
}

const CheckoutPopup: React.FC<CheckoutPopupProps> = ({ isOpen, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { cartItems, cartTotal, clearCart } = useCart();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'TR'
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [ibanInfo, setIbanInfo] = useState<any>(null);
  const [ibanAccounts, setIbanAccounts] = useState<any[]>([]);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [orderResult, setOrderResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const steps = [
    { id: 1, label: 'Bilgiler' },
    { id: 2, label: 'Email' },
    { id: 3, label: 'Doğrulama' },
    { id: 4, label: 'Ödeme' }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // PDF dosyası kontrolü
      if (file.type !== 'application/pdf') {
        setError('Lütfen sadece PDF dosyası yükleyin');
        return;
      }
      
      // Dosya boyutu kontrolü (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      
      setReceiptFile(file);
      setError('');
    }
  };

  const handleRemoveFile = () => {
    setReceiptFile(null);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleNext = async () => {
    setError('');
    setSuccess('');

    if (currentStep === 1) {
      // Form validasyonu
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone || !formData.address || !formData.city) {
        setError('Lütfen tüm gerekli alanları doldurun (Ad, Soyad, Email, Telefon, Adres, Şehir)');
        return;
      }
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Email doğrulama kodu gönder
      setLoading(true);
      try {
        await VerificationAPI.sendVerificationCode(formData.email, language);
        setSuccess(language === 'tr' ? 'Doğrulama kodu email adresinize gönderildi' : 'Verification code sent to your email');
        setCurrentStep(3);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 3) {
      // Doğrulama kodunu kontrol et
      if (!verificationCode || verificationCode.length !== 6) {
        setError('Lütfen 6 haneli doğrulama kodunu girin');
        return;
      }
      setLoading(true);
      try {
        const response = await VerificationAPI.verifyCode(verificationCode);
        setIbanInfo(response.iban_info);
        setIbanAccounts(response.iban_accounts || [response.iban_info]);
        setSuccess('Doğrulama başarılı! Ödeme bilgileriniz hazırlandı');
        setCurrentStep(4);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    } else if (currentStep === 4) {
      // Dekont dosyası kontrolü
      if (!receiptFile) {
        setError('Lütfen ödeme dekontunu yükleyin');
        return;
      }
      
      // Siparişi oluştur
      setLoading(true);
      try {
        // Önce PDF'i yükle
        const formDataUpload = new FormData();
        formDataUpload.append('file', receiptFile);
        
        const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/upload-receipt`, {
          method: 'POST',
          body: formDataUpload
        });
        
        const uploadResult = await uploadResponse.json();
        
        if (!uploadResult.success) {
          throw new Error(uploadResult.error || 'Dosya yüklenemedi');
        }
        
        // Kargo bilgilerini localStorage'dan al
        const selectedCargo = localStorage.getItem('selectedCargo') || '';
        const cargoPrice = localStorage.getItem('cargoPrice') || '0';
        
        // Siparişi oluştur (yüklenen dosya adı ile)
        const orderData = {
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postalCode,
          country: formData.country,
          total_amount: cartTotal + parseInt(cargoPrice),
          receipt_filename: uploadResult.filename,
          cargo_company: selectedCargo,
          cargo_price: parseInt(cargoPrice),
          language: language
        };
        
        const result = await OrderAPI.createOrder(orderData);
        console.log('Order Result:', result);
        
        // Sepeti temizle
        await clearCart();
        
        // Popup'ı kapat
        onClose();
        
        // Başarı sayfasına yönlendir
        navigate('/order-success', {
          state: {
            order_id: result.order_id,
            customer_id: result.customer_id
          }
        });
      } catch (error: any) {
        setError('Sipariş tamamlanırken bir hata oluştu: ' + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setError('');
      setSuccess('');
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <FormGroup>
              <Label>{language === 'tr' ? 'Ad *' : 'First Name *'}</Label>
              <Input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Adınızı girin' : 'Enter your first name'}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>{language === 'tr' ? 'Soyad *' : 'Last Name *'}</Label>
              <Input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Soyadınızı girin' : 'Enter your last name'}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Email *</Label>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Email adresinizi girin' : 'Enter your email address'}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>{language === 'tr' ? 'Telefon *' : 'Phone *'}</Label>
              <Input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Telefon numaranızı girin' : 'Enter your phone number'}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>{language === 'tr' ? 'Adres *' : 'Address *'}</Label>
              <Input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Adresinizi girin' : 'Enter your address'}
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>{language === 'tr' ? 'Şehir *' : 'City *'}</Label>
              <Input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                placeholder={language === 'tr' ? 'Şehrinizi girin' : 'Enter your city'}
                required
              />
            </FormGroup>
          </>
        );

      case 2:
        return (
          <InfoBox>
            <InfoIcon>📧</InfoIcon>
            <InfoText>
              {language === 'tr' 
                ? <><strong>{formData.email}</strong> adresine doğrulama kodu gönderilecek.<br />Doğrulama kodu 10 dakika geçerlidir.</>
                : <>A verification code will be sent to <strong>{formData.email}</strong>.<br />The verification code is valid for 10 minutes.</>
              }
            </InfoText>
          </InfoBox>
        );

      case 3:
        return (
          <InfoBox>
            <InfoIcon>🔐</InfoIcon>
            <InfoText>
              {language === 'tr' 
                ? <><strong>{formData.email}</strong> adresine gönderilen 6 haneli kodu girin.</>
                : <>Enter the 6-digit code sent to <strong>{formData.email}</strong>.</>
              }
            </InfoText>
            <FormGroup style={{ marginTop: '1.5rem' }}>
              <VerificationCodeInput
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                maxLength={6}
              />
            </FormGroup>
          </InfoBox>
        );

      case 4:
        return (
          <>
            <InfoBox>
              <InfoIcon>🏦</InfoIcon>
              <InfoText>
                {language === 'tr' 
                  ? 'Ödeme bilgileriniz hazırlandı. Aşağıdaki hesaplardan birine ödeme yapabilirsiniz.'
                  : 'Your payment information is ready. You can make payment to one of the accounts below.'}
              </InfoText>
              {ibanAccounts.map((account, index) => (
                <IbanInfo key={index} style={{ marginBottom: index < ibanAccounts.length - 1 ? '1rem' : '0' }}>
                  <IbanRow>
                    <IbanLabel>{language === 'tr' ? 'Banka:' : 'Bank:'}</IbanLabel>
                    <IbanValue>{account.bank_name} {account.currency && `(${account.currency})`}</IbanValue>
                  </IbanRow>
                  <IbanRow>
                    <IbanLabel>IBAN:</IbanLabel>
                    <IbanValue style={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>{account.iban}</IbanValue>
                  </IbanRow>
                  {account.swift && (
                    <IbanRow>
                      <IbanLabel>SWIFT:</IbanLabel>
                      <IbanValue>{account.swift}</IbanValue>
                    </IbanRow>
                  )}
                  <IbanRow>
                    <IbanLabel>Hesap Sahibi:</IbanLabel>
                    <IbanValue>{account.account_holder}</IbanValue>
                  </IbanRow>
                  <IbanRow>
                    <IbanLabel>Ödenecek Tutar:</IbanLabel>
                    <IbanValue>₺{account.amount}</IbanValue>
                  </IbanRow>
                </IbanInfo>
              ))}
            </InfoBox>

            <div>
              <Label>Ödeme Dekontu *</Label>
              <InfoText style={{ marginBottom: '1rem', fontSize: '0.875rem' }}>
                Ödemenizi yaptıktan sonra dekontunuzu PDF formatında yükleyin.
              </InfoText>
              
              {!receiptFile ? (
                <FileUploadArea onClick={() => document.getElementById('receipt-upload')?.click()}>
                  <FileUploadIcon>📄</FileUploadIcon>
                  <FileUploadText>Dekont PDF'ini yüklemek için tıklayın</FileUploadText>
                  <FileUploadSubtext>Maksimum dosya boyutu: 5MB</FileUploadSubtext>
                  <FileUploadInput
                    id="receipt-upload"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handleFileUpload}
                  />
                </FileUploadArea>
              ) : (
                <FilePreview>
                  <FileIcon>📄</FileIcon>
                  <FileInfo>
                    <FileName>{receiptFile.name}</FileName>
                    <FileSize>{formatFileSize(receiptFile.size)}</FileSize>
                  </FileInfo>
                  <RemoveFileButton onClick={handleRemoveFile}>
                    Kaldır
                  </RemoveFileButton>
                </FilePreview>
              )}
            </div>
          </>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>{language === 'tr' ? 'Siparişi Tamamla' : 'Complete Order'}</Title>
            <CloseButton onClick={onClose}>×</CloseButton>
          </Header>

          <Content>
            <StepIndicator>
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <Step
                    active={currentStep === step.id}
                    completed={currentStep > step.id}
                  >
                    {currentStep > step.id ? '✓' : step.id}
                  </Step>
                  {index < steps.length - 1 && (
                    <StepLine completed={currentStep > step.id} />
                  )}
                </React.Fragment>
              ))}
            </StepIndicator>

            {error && <ErrorMessage>{error}</ErrorMessage>}
            {success && <SuccessMessage>{success}</SuccessMessage>}

            {renderStepContent()}

            <Button onClick={handleNext} disabled={loading}>
              {loading ? (language === 'tr' ? 'İşleniyor...' : 'Processing...') : 
               currentStep === 1 ? (language === 'tr' ? 'Devam Et' : 'Continue') :
               currentStep === 2 ? (language === 'tr' ? 'Doğrulama Kodu Gönder' : 'Send Verification Code') :
               currentStep === 3 ? (language === 'tr' ? 'Doğrula' : 'Verify') : (language === 'tr' ? 'Siparişi Tamamla' : 'Complete Order')}
            </Button>

            {currentStep > 1 && (
              <BackButton onClick={handleBack}>
                {language === 'tr' ? 'Geri' : 'Back'}
              </BackButton>
            )}
          </Content>
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
};

export default CheckoutPopup;
